import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    grantAccessUrl: String,
    listUrl: String,
    connectUrl: String,
    syncUrl: String,
    showPopup: Boolean,
    csrfToken: String
  }

  static targets = [
    "grantAccessBtn",
    "projectSelect",
    "projectsLoading",
    "authorSelect",
    "saveBtn",
    "syncBtn",
    "errorMessage",
    "modalBody",
    "connectModal",
    "errorModal",
    "ownerNoticeModal"
  ]

  connect() {
    if (this.showPopupValue) {
      this.listProjects()
    }
  }

  grantAccess(event) {
    event.preventDefault()
    const btn = this.grantAccessBtnTarget
    btn.disabled = true
    btn.textContent = "..."
    window.location.href = this.grantAccessUrlValue
  }

  async listProjects() {
    this.openModal(this.connectModalTarget)
    this._setLoading(true)

    try {
      const response = await fetch(this.listUrlValue, {
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) {
        this.closeModal(this.connectModalTarget)
        this.showError("Something went wrong connecting to Procore. Please try again.")
        return
      }

      const data = await response.json()

      if (data.reauthorize) {
        window.location.href = this.grantAccessUrlValue
        return
      }

      if (data.error) {
        this.closeModal(this.connectModalTarget)
        this.showError(data.error)
        return
      }

      const select = this.projectSelectTarget
      select.innerHTML = `<option value="select">${select.dataset.placeholder || "Select"}...</option>`

      if (data.app_not_installed) {
        this.modalBodyTarget.innerHTML = `
          <p class="font-semibold text-red-600">The ConsLog app is not installed in your Procore account.</p>
          <p class="mt-2 text-sm text-text-muted">Please install the ConsLog app in your Procore account's App Management section, then try again.</p>
        `
      } else {
        data.project_options.forEach(([label, value]) => {
          const option = document.createElement("option")
          option.value = value
          option.textContent = label
          select.appendChild(option)
        })
      }

      this._setLoading(false)
    } catch (e) {
      this.closeModal(this.connectModalTarget)
      this.showError(e.message)
    }
  }

  _setLoading(isLoading) {
    if (this.hasProjectsLoadingTarget) {
      this.projectsLoadingTarget.classList.toggle("hidden", !isLoading)
    }
    if (this.hasProjectSelectTarget) {
      this.projectSelectTarget.disabled = isLoading
    }
    if (this.hasSaveBtnTarget) {
      this.saveBtnTarget.disabled = isLoading
    }
  }

  async saveProject(event) {
    event.preventDefault()
    const select = this.projectSelectTarget

    if (select.value === "select") {
      select.classList.add("border-red-500")
      return
    }
    select.classList.remove("border-red-500")

    const btn = this.saveBtnTarget
    btn.disabled = true
    btn.textContent = "..."

    const formData = new FormData()
    formData.append("procore_company_and_project_id", select.value)
    formData.append("procore_project_name", select.options[select.selectedIndex].text)
    formData.append("author_user_id", this.authorSelectTarget.value)

    try {
      const response = await fetch(this.connectUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfTokenValue },
        body: formData
      })
      const data = await response.json()

      if (data.reauthorize) {
        window.location.href = this.grantAccessUrlValue
        return
      }

      if (data.error) {
        this.closeModal(this.connectModalTarget)
        // Linked, but automatic updates didn't turn on: once the message is
        // closed, reload so the card shows the link and its warning.
        this.reloadOnErrorClose = !!data.linked
        this.showError(data.error)
        btn.disabled = false
        btn.textContent = "Connect"
      } else {
        window.location = window.location.href.split("?")[0]
      }
    } catch (e) {
      btn.disabled = false
      btn.textContent = "Connect"
      this.showError(e.message)
    }
  }

  async syncLogs(event) {
    event.preventDefault()
    const btn = this.syncBtnTarget
    const originalText = btn.textContent
    btn.disabled = true
    btn.textContent = "Starting sync..."
    btn.classList.remove("text-brand", "hover:underline", "cursor-pointer")
    btn.classList.add("text-text-muted")

    try {
      const response = await fetch(this.syncUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        }
      })
      const data = await response.json()

      if (data.error) {
        this.showError(data.error)
        btn.disabled = false
        btn.textContent = originalText
        btn.classList.add("text-brand", "hover:underline", "cursor-pointer")
        btn.classList.remove("text-text-muted")
        return
      }

      if (data.already) {
        // A scan is still going through the past logs: no second one.
        this._showToast(
          "A sync is already running for this project. Completed daily logs keep appearing in Reports as they're found.",
          "notice"
        )
        btn.textContent = "Sync in progress..."
        return
      }

      this._showToast(
        "Syncing historical logs from Procore. Completed daily logs will appear in Reports as they're found and processed. This may take several minutes.",
        "notice"
      )
      btn.textContent = "Sync in progress..."
    } catch (e) {
      this.showError(e.message)
      btn.disabled = false
      btn.textContent = originalText
      btn.classList.add("text-brand", "hover:underline", "cursor-pointer")
      btn.classList.remove("text-text-muted")
    }
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.openModal(this.errorModalTarget)
  }

  closeError() {
    this.closeModal(this.errorModalTarget)
    if (this.reloadOnErrorClose) window.location = window.location.href.split("?")[0]
  }

  closeConnect() {
    this.closeModal(this.connectModalTarget)
  }

  // Owners may view Procore data once a contractor connects it, but can't
  // run the integration themselves — clicking Connect shows this notice.
  ownerNotice(event) {
    event.preventDefault()
    this.openModal(this.ownerNoticeModalTarget)
  }

  closeOwnerNotice() {
    this.closeModal(this.ownerNoticeModalTarget)
  }

  // Keep clicks inside the modal panel from bubbling to the backdrop's
  // close action (backdrop-click-to-dismiss, per CLAUDE.md).
  stopPropagation(event) {
    event.stopPropagation()
  }

  openModal(modal) {
    modal.classList.remove("hidden")
  }

  closeModal(modal) {
    modal.classList.add("hidden")
  }

  _showToast(message, type) {
    const cssClass = type === "notice" ? "flash-toast-notice" : "flash-toast-alert"
    const toast = document.createElement("div")
    toast.className = cssClass
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "8000")
    toast.innerHTML = `
      <div class="flex items-center justify-between gap-3">
        <span>${message}</span>
        <button data-action="flash#dismiss" class="text-white/80 hover:text-white text-lg leading-none">&times;</button>
      </div>
    `
    document.body.prepend(toast)
  }
}
